
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Palette, Printer, Image, Layers, PenTool, Sparkles, Award, Users } from "lucide-react";

const DesignPage = () => {
  useEffect(() => {
    document.title = "Design Services - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Palette className="w-8 h-8 text-blue-600" />,
      title: "Logo Design",
      description: "Professional logo design that captures your brand's essence and creates lasting impressions"
    },
    {
      icon: <Layers className="w-8 h-8 text-blue-600" />,
      title: "Brand Identity",
      description: "Complete brand identity packages including guidelines, color schemes, and visual elements"
    },
    {
      icon: <Image className="w-8 h-8 text-blue-600" />,
      title: "Graphics Design",
      description: "Eye-catching graphics for social media, websites, advertisements, and marketing materials"
    },
    {
      icon: <Printer className="w-8 h-8 text-blue-600" />,
      title: "Print Design",
      description: "Business cards, brochures, flyers, posters, and other print marketing materials"
    },
    {
      icon: <PenTool className="w-8 h-8 text-blue-600" />,
      title: "UI/UX Design",
      description: "User-friendly interface designs and exceptional user experience for web and mobile apps"
    },
    {
      icon: <Sparkles className="w-8 h-8 text-blue-600" />,
      title: "Illustration",
      description: "Custom illustrations, icons, and visual elements for unique brand storytelling"
    }
  ];

  const designTools = [
    { name: "Adobe Photoshop", icon: "🎨" },
    { name: "Adobe Illustrator", icon: "✨" },
    { name: "Figma", icon: "🔧" },
    { name: "Adobe InDesign", icon: "📖" },
    { name: "Canva Pro", icon: "🎭" },
    { name: "Sketch", icon: "✏️" }
  ];

  const designTypes = [
    "Logo & Brand Identity",
    "Social Media Graphics",
    "Website Design",
    "Print Materials",
    "Packaging Design",
    "Digital Illustrations",
    "Infographics",
    "Presentation Design"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Professional design services in Nepal. Logo design, brand identity, graphics design, UI/UX design, and print materials." />
        <meta name="keywords" content="logo design, graphics design, brand identity, UI UX design, print design, Nepal" />
        <meta property="og:title" content="Design Services - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              Design Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Creative design solutions that bring your brand to life and captivate your audience.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Design Tools Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Design Tools We Use</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {designTools.map((tool, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{tool.icon}</div>
                  <h3 className="font-semibold">{tool.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Design Types Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Design Types We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {designTypes.map((type, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="font-medium">{type}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Design Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Design Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Brief", desc: "Understanding your vision and requirements" },
                { step: "02", title: "Concept", desc: "Creating initial design concepts and ideas" },
                { step: "03", title: "Design", desc: "Developing the final design with attention to detail" },
                { step: "04", title: "Delivery", desc: "Final files delivery and unlimited revisions" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Create Something Amazing?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Let's bring your creative vision to life with stunning designs.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default DesignPage;
